import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getPerson } from '../api';
import { Loader, Calendar, MapPin, Link2, Globe } from 'lucide-react';

export const PersonProfilePage = () => {
  const { personId } = useParams();
  const [loading, setLoading] = useState(true);
  const [person, setPerson] = useState(null);

  useEffect(() => {
    fetchPerson();
  }, [personId]);

  const fetchPerson = async () => {
    setLoading(true);
    try {
      const response = await getPerson(personId);
      setPerson(response.data);
    } catch (error) {
      console.error('Failed to fetch person:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-20 flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!person) {
    return (
      <div className="min-h-screen bg-background pt-20 flex items-center justify-center">
        <p className="text-muted-foreground">Personne non trouvée</p>
      </div>
    );
  }

  const socialLinks = [
    { url: person.twitter_url, icon: Link2, label: 'Twitter' },
    { url: person.instagram_url, icon: Link2, label: 'Instagram' },
    { url: person.facebook_url, icon: Link2, label: 'Facebook' },
    { url: person.youtube_url, icon: Link2, label: 'YouTube' },
    { url: person.tiktok_url, icon: Link2, label: 'TikTok' },
    { url: person.website_url, icon: Globe, label: 'Site Web' },
  ].filter(link => link.url);

  return (
    <div className="min-h-screen bg-background pt-20 pb-12">
      <div className="container max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg p-8 mb-8">
          <div className="flex flex-col md:flex-row items-start gap-6">
            {/* Profile Photo */}
            <div className="w-32 h-32 flex-shrink-0 bg-primary/20 rounded-full overflow-hidden">
              {person.profile_photo ? (
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/stream/${person.profile_photo}`}
                  alt={person.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-5xl font-bold text-primary">
                  {person.name.charAt(0)}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1">
              <h1 className="text-4xl font-bold text-foreground mb-3">
                {person.name}
              </h1>
              
              <div className="flex flex-wrap items-center gap-4 text-muted-foreground mb-4">
                {person.birth_date && (
                  <span className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {new Date(person.birth_date).toLocaleDateString('fr-FR')}
                  </span>
                )}
                {person.birth_place && (
                  <span className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {person.birth_place}
                  </span>
                )}
              </div>

              {/* Social Links */}
              {socialLinks.length > 0 && (
                <div className="flex flex-wrap gap-3">
                  {socialLinks.map((link, index) => {
                    const Icon = link.icon;
                    return (
                      <a
                        key={index}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2 bg-card hover:bg-accent rounded-lg transition-colors"
                        title={link.label}
                      >
                        <Icon className="w-5 h-5" />
                        <span className="text-sm">{link.label}</span>
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Biography */}
        {person.biography && (
          <div className="bg-card border border-border rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-bold text-foreground mb-4">Biographie</h2>
            <p className="text-muted-foreground whitespace-pre-wrap leading-relaxed">
              {person.biography}
            </p>
          </div>
        )}

        {/* Filmography */}
        {person.filmography && person.filmography.length > 0 && (
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-2xl font-bold text-foreground mb-6">Filmographie</h2>
            <div className="space-y-4">
              {person.filmography.map((film, index) => (
                <Link
                  key={index}
                  to={`/video/${film.video_id}`}
                  className="block p-4 bg-accent hover:bg-accent/80 rounded-lg transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground mb-1">
                        {film.video_title}
                      </h3>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        <span className="capitalize">{film.role_type}</span>
                        {film.character_name && (
                          <>
                            <span>•</span>
                            <span>Personnage: {film.character_name}</span>
                          </>
                        )}
                      </div>
                    </div>
                    {film.year && (
                      <span className="text-sm font-medium text-muted-foreground">
                        {film.year}
                      </span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
